import { useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/daisy';
import { bookType, MaterialType } from '@/models/Book';
import { getMaterialTypeOptions } from '@/utils/material/materialTypeOptions';

import MaterialTypeOptionCard from './MaterialTypeOptionCard';

export type MaterialTypeSelectorProps = {
  bookType: bookType;
  onCancel: () => void;
  onContinue: (type: MaterialType) => void;
};

type MaterialTypeForm = {
  materialType: MaterialType | null;
};

export default function MaterialTypeSelector({
  bookType,
  onCancel,
  onContinue,
}: MaterialTypeSelectorProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.materialsTab',
  });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });
  const { control, handleSubmit } = useForm<MaterialTypeForm>({
    defaultValues: { materialType: null },
  });
  const {
    field: { value, onChange },
  } = useController({
    name: 'materialType',
    control,
  });

  const options = getMaterialTypeOptions(bookType);

  const onSubmit = ({ materialType }: MaterialTypeForm) => {
    if (materialType) onContinue(materialType);
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-1">
        <h3 className="font-semibold text-base-content leading-7">
          {t('typeSelector.title')}
        </h3>
        <p className="text-sm text-base-content/70">
          {t('typeSelector.description')}
        </p>
      </div>
      <div
        role="radiogroup"
        aria-label={t('typeSelector.title')}
        className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
      >
        {options.map(option => (
          <MaterialTypeOptionCard
            key={option.type}
            option={option}
            selected={value === option.type}
            onSelect={onChange}
          />
        ))}
      </div>
      <div className="relative flex self-end gap-4 w-94">
        <Button type="button" className="grow" onClick={onCancel}>
          {tCommon('cancel')}
        </Button>
        <Button
          type="submit"
          color="primary"
          className="grow"
          disabled={!value}
        >
          {t('typeSelector.continue')}
        </Button>
      </div>
    </form>
  );
}
